import React, { useCallback, useEffect, useState } from "react";
import { Search } from "lucide-react";
import { qrtListCampaigns } from "../../apis/qrtAdmin";
import { Pager, Spinner, StatusChip, fmtDateTime, fmtMoney, num } from "./qrtUi";
import { ScopedPage } from "./QrtScopedPage";

const PAGE_SIZE = 25;
const STATUSES = ["", "draft", "scheduled", "active", "paused", "completed", "aborted", "failed"];

const CampaignsTable = ({ userId }) => {
  const [data, setData] = useState({ rows: [], total: 0 });
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("");
  const [page, setPage] = useState(0);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await qrtListCampaigns({
        user_id: userId, limit: PAGE_SIZE, offset: page * PAGE_SIZE,
        ...(search ? { search } : {}),
        ...(status ? { status } : {}),
      });
      setData(res.data?.data || { rows: [], total: 0 });
    } catch (_) {}
    finally { setLoading(false); }
  }, [userId, search, status, page]);

  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
  }, [load]);

  useEffect(() => { setPage(0); }, [userId]);

  return (
    <div className="qrt-card">
      <div className="qrt-card-head">
        <Search size={15} style={{ color: "var(--text-3)" }} />
        <input type="search" placeholder="Search campaign name…" value={search}
          onChange={(e) => { setSearch(e.target.value); setPage(0); }}
          style={{ flex: 1, border: "none", outline: "none" }} />
        <select value={status} onChange={(e) => { setStatus(e.target.value); setPage(0); }}>
          {STATUSES.map((s) => <option key={s} value={s}>{s || "All statuses"}</option>)}
        </select>
      </div>
      {loading ? <Spinner /> : (
        <table className="qrt-table">
          <thead>
            <tr>
              <th>Campaign</th>
              <th>Status</th>
              <th className="r">Sent</th>
              <th className="r">Failed</th>
              <th className="r">Revenue</th>
              <th>Phone</th>
              <th className="r">Created</th>
            </tr>
          </thead>
          <tbody>
            {data.rows.map((c) => (
              <tr key={c.campaign_id}>
                <td>
                  <div style={{ fontWeight: 600 }}>{c.campaign_name}</div>
                  <div className="sub">#{c.campaign_id}</div>
                </td>
                <td><StatusChip value={c.status} /></td>
                <td className="r">{num(c.sent)}</td>
                <td className="r">{num(c.failed)}</td>
                <td className="r" style={{ fontWeight: 700 }}>{fmtMoney(c.revenue)}</td>
                <td className="mono">{c.phone_number || "—"}</td>
                <td className="r sub">{fmtDateTime(c.created_at)}</td>
              </tr>
            ))}
            {data.rows.length === 0 && (
              <tr><td className="empty" colSpan={7}>No campaigns for this user</td></tr>
            )}
          </tbody>
        </table>
      )}
      <Pager page={page} total={data.total} pageSize={PAGE_SIZE} onPage={setPage} />
    </div>
  );
};

const QrtCampaigns = () => (
  <ScopedPage title="Campaigns" noun="campaigns">
    {(userId) => <CampaignsTable userId={userId} />}
  </ScopedPage>
);

export default QrtCampaigns;
